import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../api/api';
import { useAuth } from './useAuth';

const NOTIFICATIONS_STORAGE_KEY = 'bizbrand_notifications';
const SEEN_FEEDBACK_KEY = 'bizbrand_seen_feedback_ids';
// Dismissed notifications are hidden client-side only (nothing is deleted from
// the database); a DB-backed DismissedOn column is the planned follow-up.
const DISMISSED_IDS_KEY = 'bizbrand_dismissed_notification_ids';

const getDismissedIds = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(DISMISSED_IDS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const addDismissedId = (id) => {
  try {
    const ids = getDismissedIds();
    if (!ids.includes(id)) {
      localStorage.setItem(DISMISSED_IDS_KEY, JSON.stringify([...ids, id]));
    }
  } catch {
    // ignore localStorage failures
  }
};

const isDismissed = (notification) => getDismissedIds().includes(notification?.id);
const TASK_CREATED_EVENT = 'bizbrand:task-created';
const FEEDBACK_RECEIVED_EVENT = 'bizbrand:feedback-received';
// Keeps multiple hook instances (navbar dropdown + notifications page) in sync
const NOTIFICATIONS_UPDATED_EVENT = 'bizbrand:notifications-updated';

export const NOTIFICATION_ROUTES = {
  coach_task_assigned: '/coaching/suggestions',
  mentor_feedback: '/coaching/feedback',
  task_created: '/actionitems',
};

// Local notifications (task-created, mentor feedback) are cached in
// localStorage keyed by browser origin, not by account — call this on
// logout/login/signup so the next account in this browser doesn't inherit
// the previous account's cached mentor feedback.
export const clearCachedNotifications = () => {
  try {
    localStorage.removeItem(NOTIFICATIONS_STORAGE_KEY);
    localStorage.removeItem(SEEN_FEEDBACK_KEY);
    localStorage.removeItem(DISMISSED_IDS_KEY);
  } catch {
    // ignore localStorage failures
  }
};

export const isPersistedNotification = (notification) => notification?.source === 'db';
export const isLocalNotification = (notification) =>
  !isPersistedNotification(notification) && notification?.type !== 'mentor_message';

export const getNotificationIcon = (type) => {
  switch (type) {
    case 'task_assigned':
      return '📋';
    case 'task_created':
      return '✅';
    case 'task_due':
      return '⏰';
    case 'task_overdue':
      return '🚨';
    case 'goal_achieved':
      return '🎉';
    case 'mentor_message':
      return '💬';
    case 'mentor_feedback':
      return '📝';
    default:
      return '📢';
  }
};

export const getPriorityColor = (priority, isDarkMode) => {
  switch (priority) {
    case 'high':
      return isDarkMode ? 'border-l-red-400 bg-red-900/20' : 'border-l-red-500 bg-red-50';
    case 'medium':
      return isDarkMode ? 'border-l-amber-400 bg-amber-900/20' : 'border-l-amber-500 bg-amber-50';
    case 'low':
      return isDarkMode ? 'border-l-green-400 bg-green-900/20' : 'border-l-green-500 bg-green-50';
    default:
      return isDarkMode ? 'border-l-slate-500 bg-slate-700/30' : 'border-l-gray-500 bg-gray-50';
  }
};

export const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

const createTaskNotification = (taskData = {}) => {
  const createdAt = new Date().toISOString();
  const taskTitle =
    taskData.Title ||
    taskData.title ||
    taskData.Description ||
    taskData.TaskDesc ||
    'New task';

  const difficultyRaw = String(taskData.DifficultyLevel || taskData.difficulty || '').toLowerCase();
  let priority = 'medium';
  if (difficultyRaw === 'hard' || difficultyRaw === 'h') priority = 'high';
  if (difficultyRaw === 'easy' || difficultyRaw === 'e') priority = 'low';

  return {
    id: `task-created-${taskData.task_id || taskData.TaskID || Date.now()}`,
    type: 'task_created',
    title: 'Task Created',
    message: `You created a new task: "${taskTitle}"`,
    isRead: false,
    createdAt,
    priority
  };
};

export function useNotifications() {
  const { user } = useAuth();
  // Mentor feedback only exists for student accounts; admin, coach, employer and
  // org admin sessions would get a 403 from /coach/my-feedback every minute.
  const isStudent = !!user && !user.isAdmin && !user.isCoach && !user.isEmployer && !user.isOrgAdmin;
  const [notifications, setNotifications] = useState(() => {
    try {
      const cached = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      if (!Array.isArray(parsed)) return [];
      // Persist only local notifications (task_created, mentor_feedback);
      // drop legacy mentor_message and any cached db-sourced alerts (they re-sync).
      return parsed.filter(isLocalNotification).filter((notif) => !isDismissed(notif));
    } catch {
      return [];
    }
  });
  const initializedRef = useRef(false);

  // Persist local notifications and broadcast the full list to other hook
  // instances. Skipped on mount so a freshly mounted instance (which only has
  // localStorage data) doesn't overwrite an instance that already synced.
  useEffect(() => {
    try {
      localStorage.setItem(
        NOTIFICATIONS_STORAGE_KEY,
        JSON.stringify(notifications.filter(isLocalNotification))
      );
    } catch {
      // ignore localStorage failures
    }

    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, { detail: notifications }));
  }, [notifications]);

  // Adopt updates broadcast by other hook instances. Setting the same array
  // reference bails out of re-rendering, so broadcasts don't loop.
  useEffect(() => {
    const onUpdated = (event) => {
      if (Array.isArray(event?.detail)) setNotifications(event.detail);
    };
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, onUpdated);
  }, []);

  useEffect(() => {
    const onTaskCreated = (event) => {
      const nextNotification = createTaskNotification(event?.detail || {});
      setNotifications((prev) => [nextNotification, ...prev]);
    };

    window.addEventListener(TASK_CREATED_EVENT, onTaskCreated);
    return () => {
      window.removeEventListener(TASK_CREATED_EVENT, onTaskCreated);
    };
  }, []);

  const getSeenFeedbackIds = useCallback(() => {
    try {
      const raw = localStorage.getItem(SEEN_FEEDBACK_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }, []);

  const markFeedbackSeen = useCallback((ids) => {
    try {
      const prev = getSeenFeedbackIds();
      const merged = [...new Set([...prev, ...ids])];
      localStorage.setItem(SEEN_FEEDBACK_KEY, JSON.stringify(merged));
    } catch { /* ignore */ }
  }, [getSeenFeedbackIds]);

  const createFeedbackNotification = useCallback((fb) => {
    //const mentorName = [fb.MentorFirstName, fb.MentorLastName].filter(Boolean).join(' ') || 'Your mentor';
    // /coach/my-feedback sends the coach's name as CoachFirstName / CoachLastName
    //old const mentorName = [fb.CoachFirstName || fb.MentorFirstName, fb.CoachLastName || fb.MentorLastName].filter(Boolean).join(' ') || 'Your mentor';
    const mentorName = [fb.CoachFirstName || fb.MentorFirstName, fb.CoachLastName || fb.MentorLastName].filter(Boolean).join(' ') || 'Your coach';
    const taskNote = fb.TaskTitle ? ` on "${fb.TaskTitle}"` : '';
    const ratingNote = fb.Rating ? ` (${fb.Rating}★)` : '';
    // CreatedOn is the server's UTC time with no offset ('yyyy-MM-dd HH:mm'), so
    // read it as UTC, not local time (it showed 'Just now' for hours)
    const fbRaw = fb.CreatedOn ? String(fb.CreatedOn).trim().replace(' ', 'T') : '';
    const fbDate = fbRaw ? new Date(fbRaw.endsWith('Z') ? fbRaw : `${fbRaw}Z`) : null;
    return {
      id: `feedback-${fb.FeedbackID}`,
      type: 'mentor_feedback',
      //old title: 'New Mentor Feedback',
      title: 'New Coach Feedback',
      message: `${mentorName} sent you feedback${taskNote}${ratingNote}`,
      isRead: false,
      //old code
      //createdAt: fb.CreatedOn ? new Date(fb.CreatedOn).toISOString() : new Date().toISOString(),
      createdAt: fbDate && !Number.isNaN(fbDate.getTime()) ? fbDate.toISOString() : new Date().toISOString(),
      priority: 'high',
      source: 'local'
    };
  }, []);

  // Poll for new mentor feedback on mount and every 60s (students only)
  useEffect(() => {
    if (!isStudent) return undefined;
    let cancelled = false;
    const checkNewFeedback = async () => {
      try {
        const res = await api.get('/coach/my-feedback');
        const allFeedback = res.data.feedback || [];
        if (cancelled || allFeedback.length === 0) return;
        const seenIds = getSeenFeedbackIds();
        const newFeedback = allFeedback.filter(fb => !seenIds.includes(fb.FeedbackID));
        if (newFeedback.length > 0) {
          const newNotifs = newFeedback.map(createFeedbackNotification);
          setNotifications(prev => {
            const existingIds = new Set(prev.map(n => n.id));
            const unique = newNotifs.filter(n => !existingIds.has(n.id));
            return unique.length > 0 ? [...unique, ...prev] : prev;
          });
          markFeedbackSeen(newFeedback.map(fb => fb.FeedbackID));
        }
      } catch { /* not a student or not logged in — ignore */ }
    };
    checkNewFeedback();
    const interval = setInterval(checkNewFeedback, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isStudent, getSeenFeedbackIds, markFeedbackSeen, createFeedbackNotification]);

  useEffect(() => {
    const onFeedbackReceived = (event) => {
      const fb = event?.detail || {};
      const notif = createFeedbackNotification(fb);
      setNotifications((prev) => {
        if (prev.some(n => n.id === notif.id)) return prev;
        return [notif, ...prev];
      });
      if (fb.FeedbackID) markFeedbackSeen([fb.FeedbackID]);
    };

    window.addEventListener(FEEDBACK_RECEIVED_EVENT, onFeedbackReceived);
    return () => {
      window.removeEventListener(FEEDBACK_RECEIVED_EVENT, onFeedbackReceived);
    };
  }, [createFeedbackNotification, markFeedbackSeen]);

  // Sync persisted alerts from the backend every 60s
  useEffect(() => {
    let cancelled = false;

    const syncPersistedAlerts = async () => {
      try {
        const res = await api.get('/user/alerts');
        if (cancelled || !Array.isArray(res.data?.alerts)) return;

        setNotifications((prev) => {
          const localNotifications = prev.filter(isLocalNotification);
          const dbNotifications = res.data.alerts
            .map((alert) => ({ ...alert, source: 'db' }))
            .filter((alert) => !isDismissed(alert));
          return [...dbNotifications, ...localNotifications].sort(
            (left, right) => new Date(right.createdAt) - new Date(left.createdAt)
          );
        });
      } catch {
        // ignore notification fetch failures
      }
    };

    syncPersistedAlerts();
    const interval = setInterval(syncPersistedAlerts, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const markAsRead = async (notification) => {
    if (!notification) return;

    if (isPersistedNotification(notification)) {
      try {
        await api.patch(`/user/alerts/${notification.id}/read`);
      } catch {
        // ignore read failure, still mark locally
      }
    }

    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notification.id ? { ...notif, isRead: true } : notif
      )
    );
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/user/alerts/read-all');
    } catch {
      // ignore persisted alert failures; local notifications will still be marked read
    }

    setNotifications(prev =>
      prev.map(notif => ({ ...notif, isRead: true }))
    );
  };

  // Dismiss = soft delete. DB alerts get DismissedOn stamped server-side (the
  // row is never deleted); local synthetic notifications — and any environment
  // where migration 023 hasn't run yet — fall back to the localStorage list.
  const dismissNotification = async (notification) => {
    if (!notification) return;
    if (isPersistedNotification(notification)) {
      try {
        await api.patch(`/user/alerts/${notification.id}/dismiss`);
      } catch {
        // pre-migration or offline: hide client-side via localStorage
      }
    }
    addDismissedId(notification.id);
    setNotifications(prev => prev.filter(notif => notif.id !== notification.id));
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return { notifications, unreadCount, markAsRead, markAllAsRead, dismissNotification };
}
